import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  ArrowLeft, Search, Download, Copy, QrCode, Snowflake, Check, AlertTriangle,
  Users, CheckCircle2, DollarSign, Sparkles, Clock, CreditCard, Trash2, Pencil, Lock, MessageSquare,
} from "lucide-react";
import { api } from "@/lib/api";
import { usePreview } from "@/context/PreviewContext";
import { StatusPill, FadeIn } from "@/components/common";
import {
  ProducerShell, ProducerNav, PSectionLabel, PSpinner, useProducerTheme, chartTheme, portalSurface,
} from "@/components/producer";
import { LocationField } from "@/components/LocationField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
];

const BLUE = "#2563EB", INDIGO = "#7C3AED", AMBER = "#F59E0B", EM = "#10B981";

const FREEZE_STEPS = [
  { key: "freezing", label: "Freeze population" },
  { key: "processing", label: "Compatibility" },
  { key: "validating", label: "Validate dataset" },
  { key: "ready", label: "Goose Ready" },
];
const STEP_ORDER = ["not_started", "freezing", "processing", "validating", "ready"];

function Metric({ icon: Icon, label, value, accent, testId, hint }) {
  return (
    <div data-testid={testId} className="p-card p-5">
      <div className="flex items-center justify-between">
        <PSectionLabel>{label}</PSectionLabel>
        <Icon className="h-4 w-4" style={{ color: accent }} />
      </div>
      <p className="font-mono-g mt-3 text-4xl font-black" style={{ color: accent }}>{value}</p>
      {hint && <p className="mt-1 text-xs p-subtle">{hint}</p>}
    </div>
  );
}

function FreezeStepper({ event, onTrigger, busy, readOnly }) {
  const state = event.freeze_state || "not_started";
  const failed = state === "failed";
  const idx = STEP_ORDER.indexOf(state);
  return (
    <div className="p-card p-6" data-testid="freeze-stepper">
      <div className="flex items-center justify-between">
        <div>
          <PSectionLabel>Event readiness</PSectionLabel>
          <h3 className="font-display mt-1 text-lg font-semibold">
            {event.status === "goose_ready" ? "Goose Ready" : "Freeze & finalize"}
          </h3>
          <p className="mt-1 text-xs p-subtle" data-testid="survey-cutoff">
            Survey cutoff {event.t1_cutoff ? new Date(event.t1_cutoff).toLocaleString() : "—"}
          </p>
        </div>
        <Snowflake className="h-5 w-5 text-sky-500" />
      </div>

      <div className="mt-5 space-y-3">
        {FREEZE_STEPS.map((s) => {
          const sIdx = STEP_ORDER.indexOf(s.key);
          const done = idx >= sIdx && !failed;
          const current = state === s.key;
          return (
            <div key={s.key} className="flex items-center gap-3">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs
                ${done ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-600"
                       : current ? "border-amber-500/50 bg-amber-500/15 text-amber-600"
                       : "p-border-c p-subtle"}`}>
                {done ? <Check className="h-3.5 w-3.5" /> : sIdx + 1}
              </div>
              <span className={`text-sm ${done || current ? "" : "p-subtle"}`}>{s.label}</span>
            </div>
          );
        })}
      </div>

      {failed && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" /> Processing failed — operators alerted.
        </div>
      )}

      {event.status === "active" && !readOnly && (
        <div className="mt-5 flex flex-col gap-2">
          <Button data-testid="trigger-freeze-btn" disabled={busy} onClick={() => onTrigger("freeze", false)}
            className="p-btn">
            {busy ? "Running…" : "Run T-1 freeze now"}
          </Button>
          <button data-testid="trigger-freeze-fail-btn" disabled={busy} onClick={() => onTrigger("freeze", true)}
            className="text-xs p-subtle hover:text-red-500">Simulate processing failure</button>
        </div>
      )}
      {event.status === "goose_ready" && (
        <p className="mt-5 flex items-center gap-2 rounded-xl p-3 text-sm font-semibold p-accent-text" style={{ background: "var(--p-accent-soft)" }}>
          <Sparkles className="h-4 w-4" /> Goose Ready — {event.surveyed_count} guests frozen.
        </p>
      )}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="p-card p-6" data-testid={testId}>
      <PSectionLabel>{title}</PSectionLabel>
      <div className="mt-4 h-52 min-h-[200px]">{children}</div>
    </div>
  );
}

function DonutLegend({ data }) {
  return (
    <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1">
      {data.map((d) => (
        <span key={d.name} className="flex items-center gap-1.5 text-xs p-muted">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
          {d.name} · {d.value}
        </span>
      ))}
    </div>
  );
}

function BadgeSearch({ eventId }) {
  const [q, setQ] = useState("");
  const { data } = useQuery({
    queryKey: ["badge-search", eventId, q],
    queryFn: async () => (await api.get(`/producer/events/${eventId}/badge-search`, { params: { q } })).data,
    enabled: q.trim().length > 0,
  });
  const results = data?.results || [];
  return (
    <div className="p-card p-6" data-testid="badge-search">
      <PSectionLabel>Badge lookup</PSectionLabel>
      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 p-subtle" />
        <Input data-testid="badge-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or email…" className="pl-9" />
      </div>
      <div className="mt-4 space-y-2">
        {q && results.length === 0 && <p className="text-sm p-subtle">No matches.</p>}
        {results.map((r) => (
          <div key={r.goose_number} data-testid={`badge-result-${r.goose_number}`} className="flex items-center justify-between p-inset p-3">
            <p className="text-sm font-semibold">{r.name || <span className="p-subtle">No name on file</span>}</p>
            <span className="font-mono-g rounded-full px-3 py-1 text-sm font-bold p-accent-text" style={{ background: "var(--p-accent-soft)" }}>#{r.goose_number}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroductionsSummary({ intros, ready }) {
  const { theme } = useProducerTheme();
  const [open, setOpen] = useState(false);
  const qualifying = intros.length;
  return (
    <div className="p-card p-6" data-testid="introductions-summary">
      <div className="flex items-center justify-between">
        <div>
          <PSectionLabel>Introductions</PSectionLabel>
          <h3 className="font-display mt-1 text-lg font-semibold">70%+ matches</h3>
        </div>
        <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-600">{qualifying}</span>
      </div>
      <p className="font-mono-g mt-4 text-4xl font-black p-accent-text" data-testid="intro-count">{qualifying}</p>
      <p className="mt-1 text-sm p-muted">
        {ready
          ? (qualifying ? "qualifying introductions ready for event night." : "No 70%+ pairs in this group.")
          : "Run the T-1 freeze to generate introductions."}
      </p>
      {qualifying > 0 && (
        <Dialog open={open} onOpenChange={setOpen}>
          <Button data-testid="view-introductions-btn" onClick={() => setOpen(true)} className="p-btn-ghost mt-4 w-full">
            View all introductions
          </Button>
          <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
            <DialogHeader><DialogTitle className="font-display text-xl">Introductions · 70%+ matches</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">The full list of qualifying 70%+ compatibility pairs by Goose number, for event night.</DialogDescription>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1" data-testid="introductions-list">
              {intros.map((it, i) => (
                <div key={i} data-testid={`intro-${it.a.goose_number}-${it.b.goose_number}`}
                  className="flex items-center justify-between p-inset p-3">
                  <div className="flex items-center gap-2">
                    <Pair g={it.a} /><span className="p-subtle">×</span><Pair g={it.b} />
                  </div>
                  <span className="font-mono-g text-2xl font-black text-amber-500">{it.score}%</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Pair({ g }) {
  return (
    <div className="flex items-center gap-2">
      {g.photo
        ? <img src={g.photo} alt="" className="h-9 w-9 rounded-full object-cover ring-2" style={{ "--tw-ring-color": "var(--p-border)" }} />
        : <div className="flex h-9 w-9 items-center justify-center rounded-full p-inset"><Users className="h-4 w-4 p-subtle" /></div>}
      <span className="font-mono-g rounded-full p-inset px-2 py-0.5 text-xs font-bold">#{g.goose_number}</span>
    </div>
  );
}

function EntryKit({ event, eventId, onGuestMessaging, readOnly }) {
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };
  const download = async () => {
    try {
      const res = await api.get(`/producer/events/${eventId}/entry-kit.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `goose-${event.goose_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="p-card p-6" data-testid="entry-kit">
      <PSectionLabel>Event materials</PSectionLabel>
      <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        {event.qr_data_url && (
          <img src={event.qr_data_url} alt="Event QR" className="h-36 w-36 rounded-xl border p-border-c bg-white p-2" data-testid="entry-qr" />
        )}
        <div className="w-full min-w-0 flex-1 space-y-3">
          <div>
            <p className="text-xs p-subtle">Registration link</p>
            <button onClick={() => copy(event.attribution_url, "Registration link")}
              data-testid="copy-attribution-btn"
              className="mt-1 flex w-full items-center justify-between gap-2 rounded-lg p-inset p-hover-accent p-2.5 text-left text-xs p-muted">
              <span className="min-w-0 truncate">{event.attribution_url}</span><Copy className="h-3.5 w-3.5 shrink-0" />
            </button>
          </div>
          <div>
            <p className="text-xs p-subtle">Event-night link</p>
            <button onClick={() => copy(event.night_url, "Event-night link")}
              data-testid="copy-night-btn"
              className="mt-1 flex w-full items-center justify-between gap-2 rounded-lg p-inset p-hover-accent p-2.5 text-left text-xs p-muted">
              <span className="min-w-0 truncate">{event.night_url}</span><Copy className="h-3.5 w-3.5 shrink-0" />
            </button>
          </div>
          <Button data-testid="download-pdf-btn" onClick={download} className="p-btn-ghost w-full">
            <Download className="mr-1.5 h-4 w-4" /> Printable sign (PDF)
          </Button>
          {!readOnly && (
            <Button data-testid="guest-messaging-btn" onClick={onGuestMessaging} className="p-btn-ghost w-full">
              <MessageSquare className="mr-1.5 h-4 w-4" /> Guest messaging
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Billing({ eventId, event, onTrigger, busy, readOnly }) {
  const { data } = useQuery({
    queryKey: ["billing", eventId, event.status],
    queryFn: async () => (await api.get(`/producer/events/${eventId}/billing`)).data,
  });
  const b = data?.billing;
  return (
    <div className="p-card p-6" data-testid="billing-summary">
      <div className="flex items-center justify-between">
        <PSectionLabel>Goose fee</PSectionLabel>
        <CreditCard className="h-4 w-4 text-emerald-500" />
      </div>
      <div className="mt-4 flex items-end justify-between">
        <div>
          <p className="font-mono-g text-4xl font-black text-emerald-600">${b?.fee ?? 0}</p>
          <p className="mt-1 text-xs p-subtle">{b?.surveyed_count ?? 0} surveyed × ${data?.fee_per_guest ?? 10}</p>
        </div>
        {b?.status && <StatusPill status={b.status} testId="billing-status" />}
      </div>
      {b?.reference && <p className="mt-3 font-mono-g text-xs p-subtle">Ref: {b.reference}</p>}
      <p className="mt-3 text-xs p-subtle">Charge date: {data?.billing_date ? new Date(data.billing_date).toLocaleDateString() : "—"}</p>

      {event.status === "goose_ready" && !readOnly && (
        <div className="mt-5 flex flex-col gap-2">
          <Button data-testid="trigger-billing-btn" disabled={busy} onClick={() => onTrigger("billing", false)}
            className="btn-pill bg-emerald-500 font-bold text-white hover:bg-emerald-600">
            Record charge (Event +7)
          </Button>
          <button data-testid="trigger-billing-fail-btn" disabled={busy} onClick={() => onTrigger("billing", true)}
            className="text-xs p-subtle hover:text-red-500">Simulate payment failure</button>
        </div>
      )}
      {event.status === "payment_due" && !readOnly && (
        <Button data-testid="retry-billing-btn" disabled={busy} onClick={() => onTrigger("billing", false)}
          className="btn-pill mt-5 w-full bg-emerald-500 font-bold text-white hover:bg-emerald-600">Retry payment</Button>
      )}
      {event.status === "billed" && !readOnly && (
        <div className="mt-5 flex flex-col gap-2">
          <Button data-testid="trigger-purge-btn" disabled={busy} onClick={() => onTrigger("purge", false)}
            className="p-btn-ghost">
            <Trash2 className="mr-1.5 h-4 w-4" /> Close out & purge temporary data
          </Button>
        </div>
      )}
    </div>
  );
}

function EditEventDialog({ ev, eventId }) {
  const qc = useQueryClient();
  const { theme } = useProducerTheme();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const locks = ev.edit_locks || { timing_locked: false, ended: false };

  const openDialog = () => {
    setForm({
      name: ev.name, date: ev.date, start_time: ev.start_time, timezone: ev.timezone,
      location: ev.location, location_id: ev.location_id ?? null,
      location_details: ev.location_details ?? null, location_changed: false,
    });
    setOpen(true);
  };

  const submit = async () => {
    const patch = {};
    if (form.name.trim() && form.name.trim() !== ev.name) patch.name = form.name.trim();
    if (!locks.timing_locked) {
      if (form.date !== ev.date) patch.date = form.date;
      if (form.start_time !== ev.start_time) patch.start_time = form.start_time;
      if (form.timezone !== ev.timezone) patch.timezone = form.timezone;
    }
    if (!locks.ended && form.location_changed && (form.location || form.location_id)) {
      patch.location = form.location;
      patch.location_id = form.location_id;
      patch.location_details = form.location_details;
    }
    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); setOpen(false); return; }
    setSaving(true);
    try {
      await api.patch(`/producer/events/${eventId}`, patch);
      await qc.invalidateQueries({ queryKey: ["event", eventId] });
      await qc.invalidateQueries({ queryKey: ["dashboard", eventId] });
      await qc.invalidateQueries({ queryKey: ["producer-events"] });
      await qc.invalidateQueries({ queryKey: ["producer-locations"] });
      toast.success("Event updated");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update event");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? openDialog() : setOpen(false))}>
      <Button data-testid="edit-event-btn" onClick={openDialog} className="p-btn-ghost">
        <Pencil className="mr-1.5 h-4 w-4" /> Edit
      </Button>
      {form && (
        <DialogContent className={`goose-producer ${theme === "dark" ? "theme-dark" : ""} ${portalSurface(theme)}`}>
          <DialogHeader><DialogTitle className="font-display text-xl">Edit event</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Update your event's name, timing and location, subject to the T-1 cutoff rules.</DialogDescription>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block text-xs p-muted">Event name</Label>
              <Input data-testid="edit-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={locks.ended} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block text-xs p-muted">Date</Label>
                <Input data-testid="edit-date-input" type="date" value={form.date} disabled={locks.timing_locked}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <Label className="mb-1.5 block text-xs p-muted">Start time</Label>
                <Input data-testid="edit-time-input" type="time" value={form.start_time} disabled={locks.timing_locked}
                  onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs p-muted">Time zone</Label>
              <Select value={form.timezone} disabled={locks.timing_locked}
                onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
                <SelectTrigger data-testid="edit-tz-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className={portalSurface(theme)}>
                  {TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {locks.timing_locked && (
              <p className="flex items-center gap-1.5 text-xs text-amber-600" data-testid="timing-lock-note">
                <Lock className="h-3.5 w-3.5" /> Date, start time and time zone are locked after the T-1 cutoff.
              </p>
            )}
            <div>
              <Label className="mb-1.5 block text-xs p-muted">Location</Label>
              {locks.ended ? (
                <p className="flex items-center gap-1.5 text-xs text-amber-600">
                  <Lock className="h-3.5 w-3.5" /> This event has closed and can no longer be edited.
                </p>
              ) : (
                <LocationField
                  theme={theme}
                  value={{ location: form.location, location_id: form.location_id, location_details: form.location_details }}
                  onChange={(v) => setForm((f) => ({ ...f, location: v.location, location_id: v.location_id, location_details: v.location_details, location_changed: true }))}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="edit-save-btn" disabled={saving} onClick={submit} className="p-btn w-full">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default function ProducerDashboard() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { theme } = useProducerTheme();
  const { preview } = usePreview();
  const readOnly = !!preview;
  const ct = chartTheme(theme);
  const [busy, setBusy] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", eventId],
    queryFn: async () => (await api.get(`/producer/events/${eventId}/dashboard`)).data,
    refetchInterval: 15000,
  });
  const { data: eventData } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => (await api.get(`/producer/events/${eventId}`)).data,
  });

  const onTrigger = async (kind, simulate) => {
    setBusy(true);
    try {
      await api.post(`/producer/events/${eventId}/trigger/${kind}`, { simulate_failure: simulate });
      await qc.invalidateQueries({ queryKey: ["dashboard", eventId] });
      await qc.invalidateQueries({ queryKey: ["event", eventId] });
      await qc.invalidateQueries({ queryKey: ["billing", eventId] });
      toast.success(simulate ? "Simulated failure triggered" : `${kind} complete`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (isLoading) return <ProducerShell><ProducerNav active="events" /><PSpinner /></ProducerShell>;
  if (error) return (
    <ProducerShell><ProducerNav active="events" />
      <div className="mx-auto max-w-md px-6 py-20 text-center">
        <h2 className="font-display text-2xl font-bold">Event not found</h2>
        <Button onClick={() => navigate("/producer")} className="p-btn mt-6">Back to events</Button>
      </div>
    </ProducerShell>
  );

  const ev = { ...(data.event || {}), ...(eventData || {}) };
  const h = data.headline;
  const gender = [
    { name: "Women", value: data.gender.woman, fill: BLUE },
    { name: "Men", value: data.gender.man, fill: INDIGO },
    { name: "Non-binary", value: data.gender["non-binary"], fill: AMBER },
  ];
  const intent = [
    { name: "Long-term", value: data.intent["long-term"], fill: BLUE },
    { name: "Casual", value: data.intent.casual, fill: AMBER },
  ];
  const age = Object.entries(data.age).map(([name, value]) => ({ name, value }));
  const demoTotal = gender.reduce((s, x) => s + (x.value || 0), 0)
    + intent.reduce((s, x) => s + (x.value || 0), 0)
    + age.reduce((s, x) => s + (x.value || 0), 0);
  const hasDemographics = demoTotal > 0;

  return (
    <ProducerShell>
      <ProducerNav active="events" />
      <div className="mx-auto max-w-7xl px-6 py-8">
        <FadeIn>
          <button data-testid="back-to-events" onClick={() => navigate("/producer")} className="flex items-center gap-1.5 text-sm p-muted hover:opacity-80">
            <ArrowLeft className="h-4 w-4" /> All events
          </button>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl font-bold sm:text-4xl">{ev.name}</h1>
                <StatusPill status={ev.status} testId="event-status" />
              </div>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm p-muted">
                <span className="font-mono-g p-subtle">{ev.goose_id}</span>
                <span>{ev.date} · {ev.start_time} · {ev.timezone}</span>
                <span>{ev.location}</span>
              </p>
              <p className="mt-1 flex items-center gap-2 text-xs p-subtle">
                <Clock className="h-3.5 w-3.5" /> T-1 cutoff {ev.t1_cutoff ? new Date(ev.t1_cutoff).toLocaleString() : "—"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!readOnly && <EditEventDialog ev={ev} eventId={eventId} />}
              {!readOnly && (
                <Button data-testid="dashboard-event-setup-btn" onClick={() => navigate(`/producer/events/${eventId}/setup`)}
                  className="p-btn-ghost">
                  <QrCode className="mr-1.5 h-4 w-4" /> Event setup
                </Button>
              )}
            </div>
          </div>
        </FadeIn>

        {/* headline metrics */}
        <FadeIn delay={0.05}>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="headline-metrics">
            <Metric icon={Users} label="Registered guests" value={h.attributed} accent={BLUE} testId="metric-registered" />
            <Metric icon={CheckCircle2} label="Goose Ready" value={h.goose_enabled} accent={EM} testId="metric-ready" />
            <Metric icon={Clock} label="Still to complete" value={Math.max(h.attributed - h.goose_enabled, 0)} accent={AMBER} testId="metric-pending" />
            <Metric icon={DollarSign} label="Goose fee" value={`$${ev.fee_locked || h.goose_enabled * 10}`} accent={INDIGO} testId="metric-fee" hint="Informational only" />
          </div>
        </FadeIn>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {!hasDemographics && (
              <div className="p-card p-6" data-testid="demographics-hidden">
                <PSectionLabel>Guest demographics</PSectionLabel>
                <p className="mt-2 text-sm p-muted">Demographic charts will appear here once survey demographic data is available for this event.</p>
              </div>
            )}
            {hasDemographics && (<>
            <FadeIn delay={0.1}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <ChartCard title="Gender mix" testId="chart-gender">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={gender} dataKey="value" innerRadius={52} outerRadius={78} paddingAngle={3} stroke="none">
                        {gender.map((g, i) => <Cell key={i} fill={g.fill} />)}
                      </Pie>
                      <Tooltip contentStyle={ct.tooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Relationship intent" testId="chart-intent">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={intent} dataKey="value" innerRadius={52} outerRadius={78} paddingAngle={3} stroke="none">
                        {intent.map((g, i) => <Cell key={i} fill={g.fill} />)}
                      </Pie>
                      <Tooltip contentStyle={ct.tooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </FadeIn>
            <FadeIn delay={0.14}>
              <div className="mt-6 flex gap-8 text-xs p-muted">
                <DonutLegend data={gender} /><DonutLegend data={intent} />
              </div>
            </FadeIn>
            <FadeIn delay={0.16}>
              <div className="mt-6">
                <ChartCard title="Age distribution" testId="chart-age">
                  <ResponsiveContainer>
                    <BarChart data={age}>
                      <XAxis dataKey="name" tick={{ fill: ct.axis, fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: ct.axis, fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(37,99,235,0.08)" }} contentStyle={ct.tooltip} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} fill={BLUE} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </FadeIn>
            </>)}
            <FadeIn delay={0.2}>
              <div className="mt-6 lg:hidden">
                <IntroductionsSummary intros={data.introductions} ready={ev.status === "goose_ready"} />
              </div>
            </FadeIn>
          </div>

          <div className="space-y-6 lg:col-span-4">
            <FadeIn delay={0.1}><FreezeStepper event={ev} onTrigger={onTrigger} busy={busy} readOnly={readOnly} /></FadeIn>
            <FadeIn delay={0.13} className="hidden lg:block"><IntroductionsSummary intros={data.introductions} ready={ev.status === "goose_ready"} /></FadeIn>
            <FadeIn delay={0.14}><BadgeSearch eventId={eventId} /></FadeIn>
            <FadeIn delay={0.18}><Billing eventId={eventId} event={ev} onTrigger={onTrigger} busy={busy} readOnly={readOnly} /></FadeIn>
            <FadeIn delay={0.22}><EntryKit event={ev} eventId={eventId} readOnly={readOnly} onGuestMessaging={() => navigate(`/producer/events/${eventId}/setup`)} /></FadeIn>
          </div>
        </div>
      </div>
    </ProducerShell>
  );
}
